const MAX_FILE_SIZE_MB = 30;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
const VALID_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const SERVICE_UNAVAILABLE_MESSAGE = "Due to heavy traffic we can't serve you now, please come back later!";
const CONTENT_SAFETY_MESSAGE = 'This photo cannot be used for preview generation because it does not meet our content safety guidelines. Please upload a clear, non-explicit photo and try again.';
const PROXY_BASE = '/apps/miniaturewallah';
const POLL_INTERVAL = 4000;
const MAX_POLL_ATTEMPTS = 450;
const LOCAL_PROGRESS_INTERVAL = 10000;
const GENERATE_BUTTON_LABEL = 'Generate Preview';
const PERSON_NAME_MAX_LENGTH = 50;
const MINIATURE_QUOTE_MAX_LENGTH = 70;
const GENERATED_PREVIEW_PAGE_SIZE = 3;
const PHOTO_UPLOAD_STEP_ID = 'mw-photo-upload-step';
const PHOTO_UPLOAD_RETURN_PARAM = 'mw_upload_step';
const ALLOWED_PROFESSIONS = new Set([
  'doctor',
  'lawyer',
  'police',
  'teacher',
  'engineer',
  'firefighter',
  'nurse',
  'pilot',
  'chef',
  'scientist',
  'astronaut',
  'soldier',
  'judge',
  'architect',
  'artist',
  'ias',
  'sameuniform',
]);
const ALLOWED_SIZES = new Set(['bust', 'full', 'bustnohand']);

const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

class MwPersonalizedUpload extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.serviceAvailable = this.dataset.serviceAvailable === 'true';
    this.orderingOpen = this.dataset.orderingOpen === 'true';
    this.profession = this.dataset.profession || 'personalized';
    this.size = this.dataset.size || 'full';
    this.miniatureStyle = this.dataset.miniatureStyle || 'Personalized';
    this.miniatureDisplaySize = this.dataset.miniatureDisplaySize || '';
    this.quoteEnabled = this.dataset.quoteEnabled === 'true';
    this.personNameRequired = this.dataset.personNameRequired === 'true';
    this.productPrice = this.dataset.productPrice || '';
    this.variantPrices = this.parseJsonDataset(this.dataset.variantPrices, {});
    this.form = document.getElementById(this.dataset.productFormId);
    this.input = this.querySelector('[data-mw-photo-input]');
    this.dropzone = this.querySelector('[data-mw-dropzone]');
    this.personNameInput = this.querySelector('[data-mw-person-name]');
    this.quoteInput = this.querySelector('[name="properties[Miniature Quote]"]');
    this.personNameLimit = this.querySelector('[data-mw-person-name-limit]');
    this.quoteLimit = this.querySelector('[data-mw-quote-limit]');
    this.selection = this.querySelector('[data-mw-selection]');
    this.preview = this.querySelector('[data-mw-preview]');
    this.fileName = this.querySelector('[data-mw-file-name]');
    this.fileStatus = this.querySelector('[data-mw-file-status]');
    this.uploadError = this.querySelector('[data-mw-upload-error]');
    this.loginAlert = this.querySelector('[data-mw-login-alert]');
    this.error = this.querySelector('[data-mw-error]');
    this.removeButton = this.querySelector('[data-mw-remove]');
    this.replaceButton = this.querySelector('[data-mw-replace]');
    this.uploadButton = this.querySelector('[data-mw-upload-photo]');
    this.generateButton = this.querySelector('[data-mw-generate-model]');
    this.generationProgress = this.querySelector('[data-mw-generation-progress]');
    this.generationProgressBar = this.querySelector('[data-mw-generation-progress-bar]');
    this.generationProgressText = this.querySelector('[data-mw-generation-progress-text]');
    this.modelPanel = this.querySelector('[data-mw-model-panel]');
    this.generatedViewer = this.querySelector('[data-mw-generated-viewer]');
    this.generatedLibrary = this.querySelector('[data-mw-generated-library]');
    this.generatedGrid = this.querySelector('[data-mw-generated-grid]');
    this.generatedCount = this.querySelector('[data-mw-generated-count]');
    this.generatedPrev = this.querySelector('[data-mw-generated-prev]');
    this.generatedNext = this.querySelector('[data-mw-generated-next]');
    this.library = this.querySelector('[data-mw-photo-library]');
    this.photoGrid = this.querySelector('[data-mw-photo-grid]');
    this.photoCount = this.querySelector('[data-mw-photo-count]');
    this.photoPrev = this.querySelector('[data-mw-photo-prev]');
    this.photoNext = this.querySelector('[data-mw-photo-next]');
    this.photoProperty = this.querySelector('[data-mw-photo-property]');
    this.jobProperty = this.querySelector('[data-mw-job-property]');
    this.sizePicker = this.querySelector('[data-mw-size-picker]');
    this.sizeNavButton = this.querySelector('[data-mw-size-nav]');
    this.sizeMenu = this.querySelector('[data-mw-size-menu]');
    this.sizeButtonLabel = this.querySelector('[data-mw-size-button-label]');
    this.sizeOptions = Array.from(this.querySelectorAll('[data-mw-size-option]'));

    this.previewUrl = '';
    this.photos = [];
    this.photoPageStart = 0;
    this.selectedPhoto = null;
    this.generatedModel = null;
    this.generatedPreviews = [];
    this.generatedPreviewPage = 1;
    this.generatedPreviewTotal = 0;
    this.deletingGeneratedJobIds = new Set();
    this.isUploading = false;
    this.isGenerating = false;
    this.loginRequired = false;
    this.generationProgressTimer = null;
    this.deletingPhotoIds = new Set();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.purchaseConfirmationBypassUntil = 0;
    this.form?.addEventListener('submit', (event) => this.guardPurchase(event, event.submitter), { capture: true, signal });
    document.addEventListener('click', (event) => this.handlePurchaseClick(event), { capture: true, signal });

    if (!this.orderingOpen) return;

    if (!this.serviceAvailable) {
      this.setServiceUnavailable(this.dataset.unavailableMessage || SERVICE_UNAVAILABLE_MESSAGE);
      return;
    }

    this.input?.addEventListener('change', () => this.handleFileSelection(), { signal });
    this.removeButton?.addEventListener('click', () => this.clearFile(), { signal });
    this.replaceButton?.addEventListener('click', () => this.input?.click(), { signal });
    this.uploadButton?.addEventListener('click', () => this.uploadSelectedPhoto(), { signal });
    this.generateButton?.addEventListener('click', () => this.generateModel(), { signal });
    this.personNameInput?.addEventListener(
      'input',
      () => {
        this.updateCharacterLimitMessage(this.personNameInput, this.personNameLimit, PERSON_NAME_MAX_LENGTH);
        this.invalidateGeneratedPreviewForDetailChange();
      },
      { signal }
    );
    this.quoteInput?.addEventListener(
      'input',
      () => {
        this.updateCharacterLimitMessage(this.quoteInput, this.quoteLimit, MINIATURE_QUOTE_MAX_LENGTH);
        this.invalidateGeneratedPreviewForDetailChange();
      },
      { signal }
    );
    this.dropzone?.addEventListener('click', (event) => this.guardLoginBeforeUploadPicker(event), { capture: true, signal });
    this.input?.addEventListener('click', (event) => this.guardLoginBeforeUploadPicker(event), { capture: true, signal });
    this.photoPrev?.addEventListener('click', () => this.scrollPhotoLibrary(-1), { signal });
    this.photoNext?.addEventListener('click', () => this.scrollPhotoLibrary(1), { signal });
    this.generatedPrev?.addEventListener('click', () => this.loadGeneratedPreviewLibrary(this.generatedPreviewPage - 1), { signal });
    this.generatedNext?.addEventListener('click', () => this.loadGeneratedPreviewLibrary(this.generatedPreviewPage + 1), { signal });
    this.sizeNavButton?.addEventListener('click', () => this.toggleSizeMenu(), { signal });
    this.sizeOptions.forEach((option) => {
      option.addEventListener('click', () => this.selectSizeOption(option), { signal });
    });
    document.addEventListener('click', (event) => this.closeSizeMenuOnOutsideClick(event), { signal });
    document.addEventListener('keydown', (event) => this.handleSizeMenuKeydown(event), { signal });
    this.dropzone?.addEventListener(
      'dragover',
      (event) => {
        event.preventDefault();
        this.dropzone.classList.add('is-dragging');
      },
      { signal }
    );
    this.dropzone?.addEventListener('dragleave', () => this.dropzone.classList.remove('is-dragging'), { signal });
    this.dropzone?.addEventListener('drop', (event) => this.handleDrop(event), { signal });

    this.loadPhotoLibrary();
    this.loadGeneratedPreviewLibrary();
    this.updateCharacterLimitMessage(this.personNameInput, this.personNameLimit, PERSON_NAME_MAX_LENGTH);
    this.updateCharacterLimitMessage(this.quoteInput, this.quoteLimit, MINIATURE_QUOTE_MAX_LENGTH);
    this.scrollToUploadStepAfterLogin();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.stopGenerationProgressTimer();
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
  }

  proxyHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    if (
      window.location.hostname.includes('ngrok') ||
      window.location.hostname.includes('myshopify') ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === 'localhost'
    ) {
      headers['ngrok-skip-browser-warning'] = 'true';
    }
    return headers;
  }

  async proxyFetch(path, options = {}) {
    let response;
    try {
      response = await fetch(`${PROXY_BASE}${path}`, {
        method: 'POST',
        credentials: 'same-origin',
        ...options,
        headers: {
          ...this.proxyHeaders(),
          ...(options.headers || {}),
        },
      });
    } catch (error) {
      const serviceError = new Error(SERVICE_UNAVAILABLE_MESSAGE);
      serviceError.status = 0;
      serviceError.originalError = error;
      throw serviceError;
    }

    let data = null;
    const text = await response.text();
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { detail: text };
      }
    }

    if (this.isHtmlResponse(response, text)) {
      const error = new Error(SERVICE_UNAVAILABLE_MESSAGE);
      error.status = 503;
      error.data = data;
      throw error;
    }

    if (!response.ok) {
      const message = this.getApiErrorMessage(response.status, data);
      const error = new Error(message);
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data;
  }

  isHtmlResponse(response, text) {
    const contentType = response.headers.get('content-type') || '';
    const trimmed = String(text || '').trim().toLowerCase();
    return contentType.includes('text/html') || trimmed.startsWith('<!doctype') || trimmed.startsWith('<html');
  }

  getApiErrorMessage(status, data) {
    const detail = data?.detail;
    if (this.isProxyConfigurationError(data)) return SERVICE_UNAVAILABLE_MESSAGE;
    if (this.isLoginRequiredStatus(status, data)) return 'Please sign in before uploading photos for a custom miniature.';
    if (!status || status === 408 || status === 429 || status >= 500) return SERVICE_UNAVAILABLE_MESSAGE;
    if (this.isContentSafetyError(data)) return CONTENT_SAFETY_MESSAGE;
    if (Array.isArray(detail)) return detail.map((item) => item.msg).filter(Boolean).join(' ') || 'Please check the details and try again.';
    if (typeof detail === 'string' && this.looksLikeBackendUnavailable(detail)) return SERVICE_UNAVAILABLE_MESSAGE;
    if (typeof detail === 'string') return detail;
    return 'Something went wrong. Please try again.';
  }

  getCustomerSafeErrorMessage(error, fallback = 'Something went wrong. Please try again.') {
    if (this.isContentSafetyError(error?.data) || this.isContentSafetyError(error?.message) || this.isContentSafetyError(error)) {
      return CONTENT_SAFETY_MESSAGE;
    }

    return error?.message || fallback;
  }

  isContentSafetyError(data) {
    const text = this.flattenApiErrorText(data).toLowerCase();
    return (
      text.includes('moderation_blocked') ||
      text.includes('safety system') ||
      text.includes('safety_violations') ||
      text.includes('image_generation_user_error') ||
      text.includes("categories': ['sexual") ||
      text.includes('"categories":["sexual') ||
      text.includes('sexual')
    );
  }

  flattenApiErrorText(value) {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) return value.map((item) => this.flattenApiErrorText(item)).join(' ');
    if (typeof value === 'object') return Object.values(value).map((item) => this.flattenApiErrorText(item)).join(' ');
    return '';
  }

  isProxyConfigurationError(data) {
    const text = this.flattenApiErrorText(data).toLowerCase();
    return (
      text.includes('invalid shopify proxy signature') ||
      text.includes('proxy signature') ||
      text.includes('invalid signature') ||
      text.includes('hmac')
    );
  }

  isLoginRequiredStatus(status, data) {
    if (status !== 401 && status !== 403) return false;
    if (this.isProxyConfigurationError(data)) return false;

    const text = this.flattenApiErrorText(data).toLowerCase();
    return (
      !text ||
      text.includes('login') ||
      text.includes('log in') ||
      text.includes('sign in') ||
      text.includes('customer') ||
      text.includes('unauthorized')
    );
  }

  looksLikeBackendUnavailable(value) {
    const text = value.toLowerCase();
    return (
      text.includes('bad gateway') ||
      text.includes('service unavailable') ||
      text.includes('gateway timeout') ||
      text.includes('failed to fetch') ||
      text.includes('ngrok') ||
      text.includes('<html') ||
      text.includes('<!doctype')
    );
  }

  isServiceUnavailableError(error) {
    return (
      error?.status === 0 ||
      error.status === 408 ||
      error.status === 429 ||
      error.status >= 500 ||
      this.isProxyConfigurationError(error?.data || error?.message || error) ||
      error.message === SERVICE_UNAVAILABLE_MESSAGE ||
      this.looksLikeBackendUnavailable(error.message || '')
    );
  }

  async loadPhotoLibrary() {
    try {
      const data = await this.proxyFetch('/photos/list', { body: JSON.stringify({}) });
      this.loginRequired = false;
      this.renderPhotoLibrary(data?.photos || []);
      this.hideError();
      this.hideLoginAlert();
    } catch (error) {
      if (this.isLoginRequiredStatus(error.status, error.data)) {
        this.showLoginRequired();
        return;
      }
      this.setServiceUnavailable(error.message || SERVICE_UNAVAILABLE_MESSAGE);
    }
  }

  showLoginRequired() {
    this.loginRequired = true;
    this.input?.removeAttribute('disabled');
    this.dropzone?.classList.remove('is-disabled');
    this.dropzone?.setAttribute('aria-disabled', 'false');
    this.setUploadButtonState(false, 'Upload photo');
    this.setGenerateState(false, GENERATE_BUTTON_LABEL);
    this.enablePurchaseButtons();
    this.showLoginAlert();
  }

  guardLoginBeforeUploadPicker(event) {
    if (!this.loginRequired) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.showSignInPrompt();
  }

  showLoginAlert() {
    if (!this.loginAlert) {
      this.showError('Please sign in before uploading photos.');
      return;
    }

    this.loginAlert.textContent = 'Sign in is required before uploading photos or generating your preview. ';
    const link = document.createElement('a');
    link.href = this.getLoginUrl();
    link.textContent = 'Sign in';
    link.className = 'mw-personalized__login-link';
    this.loginAlert.append(link);
    this.loginAlert.hidden = false;
  }

  getLoginUrl() {
    const returnUrl = new URL(window.location.href);
    returnUrl.searchParams.set(PHOTO_UPLOAD_RETURN_PARAM, '1');
    returnUrl.hash = PHOTO_UPLOAD_STEP_ID;
    const returnPath = `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`;
    return `/customer_authentication/login?return_to=${encodeURIComponent(returnPath)}`;
  }

  scrollToUploadStepAfterLogin() {
    const shouldScroll =
      window.location.hash === `#${PHOTO_UPLOAD_STEP_ID}` ||
      new URLSearchParams(window.location.search).get(PHOTO_UPLOAD_RETURN_PARAM) === '1';

    if (!shouldScroll) return;

    const scrollToTarget = () => {
      const target = document.getElementById(PHOTO_UPLOAD_STEP_ID) || this.dropzone || this;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.focus?.({ preventScroll: true });
    };

    window.setTimeout(scrollToTarget, 250);
    window.setTimeout(scrollToTarget, 900);
    window.setTimeout(() => {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete(PHOTO_UPLOAD_RETURN_PARAM);
      if (cleanUrl.hash === `#${PHOTO_UPLOAD_STEP_ID}`) cleanUrl.hash = '';
      window.history.replaceState(window.history.state, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
    }, 1200);
  }

  scrollToVariantPicker() {
    const picker = this.getVariantPicker();
    if (!picker) return;

    picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
    picker.classList.add('mw-personalized-variant-focus');

    window.setTimeout(() => {
      const focusTarget =
        picker.querySelector('input:checked:not([aria-disabled="true"])') ||
        picker.querySelector('select') ||
        picker.querySelector('input:not([aria-disabled="true"])');

      focusTarget?.focus?.({ preventScroll: true });
    }, 350);

    window.setTimeout(() => {
      picker.classList.remove('mw-personalized-variant-focus');
    }, 1800);
  }

  getVariantPicker() {
    return document.querySelector('variant-picker[data-template-product-match="true"]') || document.querySelector('variant-picker');
  }

  toggleSizeMenu() {
    if (!this.sizeMenu) {
      this.scrollToVariantPicker();
      return;
    }

    const willOpen = this.sizeMenu.hidden;
    this.sizeMenu.hidden = !willOpen;
    this.sizeNavButton?.setAttribute('aria-expanded', String(willOpen));

    if (willOpen) {
      const selectedOption = this.sizeMenu.querySelector('[data-mw-size-option][aria-pressed="true"]');
      const firstAvailableOption = this.sizeMenu.querySelector('[data-mw-size-option]:not(:disabled)');
      (selectedOption || firstAvailableOption)?.focus();
    }
  }

  closeSizeMenu({ restoreFocus = false } = {}) {
    if (!this.sizeMenu || this.sizeMenu.hidden) return;
    this.sizeMenu.hidden = true;
    this.sizeNavButton?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.sizeNavButton?.focus();
  }

  closeSizeMenuOnOutsideClick(event) {
    if (!(event.target instanceof Node) || this.sizePicker?.contains(event.target)) return;
    this.closeSizeMenu();
  }

  handleSizeMenuKeydown(event) {
    if (event.key !== 'Escape' || this.sizeMenu?.hidden) return;
    event.preventDefault();
    this.closeSizeMenu({ restoreFocus: true });
  }

  selectSizeOption(option) {
    const optionValueId = option.dataset.optionValueId;
    const picker = this.getVariantPicker();
    const pickerOption = optionValueId ? picker?.querySelector(`[data-option-value-id="${CSS.escape(optionValueId)}"]`) : null;

    if (!pickerOption) {
      this.closeSizeMenu();
      this.scrollToVariantPicker();
      return;
    }

    if (pickerOption instanceof HTMLOptionElement) {
      const select = pickerOption.closest('select');
      if (!select) return;
      select.value = pickerOption.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (pickerOption instanceof HTMLInputElement) {
      pickerOption.click();
    }

    this.sizeOptions.forEach((sizeOption) => {
      const selected = sizeOption === option;
      sizeOption.classList.toggle('is-selected', selected);
      sizeOption.setAttribute('aria-pressed', String(selected));
    });

    if (this.sizeButtonLabel) this.sizeButtonLabel.textContent = option.dataset.sizeLabel || 'Select size';
    this.closeSizeMenu({ restoreFocus: true });
  }

  showSignInPrompt() {
    const existingModal = document.querySelector('[data-mw-login-prompt-modal]');
    if (existingModal) existingModal.remove();

    const modal = document.createElement('div');
    modal.className = 'mw-personalized__login-modal';
    modal.dataset.mwLoginPromptModal = 'true';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'MwLoginPromptTitle');
    modal.innerHTML = `
      <div class="mw-personalized__login-modal-card">
        <button type="button" class="mw-personalized__login-modal-close" aria-label="Close sign in prompt">&times;</button>
        <p class="mw-personalized__login-modal-eyebrow">Sign in required</p>
        <h3 id="MwLoginPromptTitle">Sign in to continue your custom miniature</h3>
        <p>Upload photos and generate previews are saved to your account, so please sign in before continuing.</p>
        <a class="mw-personalized__login-modal-button" href="${this.escapeAttribute(this.getLoginUrl())}">Sign in to continue</a>
      </div>
    `;

    const close = () => modal.remove();
    modal.addEventListener('click', (event) => {
      if (event.target === modal) close();
    });
    modal.querySelector('.mw-personalized__login-modal-close')?.addEventListener('click', close);
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') close();
      },
      { once: true }
    );

    document.body.append(modal);
    modal.querySelector('.mw-personalized__login-modal-button')?.focus();
  }

  hideLoginAlert() {
    if (!this.loginAlert) return;
    this.loginAlert.hidden = true;
    this.loginAlert.textContent = '';
  }

  setServiceUnavailable(message) {
    this.serviceAvailable = false;
    this.clearPreview();
    this.clearSelectedPhotoState();
    if (this.library) this.library.hidden = true;
    if (this.photoGrid) this.photoGrid.innerHTML = '';
    if (this.generatedViewer) {
      this.generatedViewer.hidden = true;
      this.generatedViewer.innerHTML = '';
    }
    if (this.generatedLibrary) this.generatedLibrary.hidden = true;
    if (this.generatedGrid) this.generatedGrid.innerHTML = '';
    this.input?.setAttribute('disabled', 'disabled');
    this.dropzone?.classList.add('is-disabled');
    this.dropzone?.setAttribute('aria-disabled', 'true');
    this.disableGenerateButton();
    this.disablePurchaseButtons();
    this.showError(message || SERVICE_UNAVAILABLE_MESSAGE);
  }

  renderPhotoLibrary(photos) {
    if (!this.library || !this.photoGrid) return;

    this.photos = Array.isArray(photos) ? photos : [];
    this.photoPageStart = Math.min(this.photoPageStart, Math.max(0, this.photos.length - 1));
    if (!this.photos.length) {
      this.photoGrid.innerHTML = '';
      this.library.hidden = true;
      return;
    }

    this.library.hidden = false;
    this.renderPhotoWindow();
  }

  getPhotoWindowSize() {
    return window.matchMedia('(max-width: 749px)').matches ? 2 : 3;
  }

  renderPhotoWindow() {
    if (!this.photoGrid) return;

    const windowSize = this.getPhotoWindowSize();
    const maxStart = Math.max(0, this.photos.length - windowSize);
    this.photoPageStart = Math.min(this.photoPageStart, maxStart);
    const visiblePhotos = this.photos.slice(this.photoPageStart, this.photoPageStart + windowSize);
    const visibleEnd = Math.min(this.photoPageStart + visiblePhotos.length, this.photos.length);

    if (this.photoCount) {
      this.photoCount.textContent =
        this.photos.length > windowSize
          ? `${this.photoPageStart + 1}-${visibleEnd} of ${this.photos.length}`
          : `${this.photos.length} saved`;
    }

    this.photoGrid.innerHTML = '';
    visiblePhotos.forEach((photo) => {
      const card = document.createElement('div');
      card.className = 'mw-personalized__photo-card';
      card.dataset.photoId = String(photo.id);
      card.classList.toggle('is-selected', String(this.selectedPhoto?.id) === String(photo.id));
      card.innerHTML = `
        <button type="button" class="mw-personalized__photo-select" data-mw-select-photo>
          <img src="${photo.view_url}" alt="Uploaded customer photo" loading="lazy">
          <span>Uploaded photo</span>
        </button>
        <button type="button" class="mw-personalized__photo-delete" data-mw-delete-photo aria-label="Delete uploaded photo">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M9 3h6l1 2h4v2H4V5h4l1-2Z"></path>
            <path d="M6 9h12l-.7 11H6.7L6 9Zm4 2v7h1.6v-7H10Zm2.4 0v7H14v-7h-1.6Z"></path>
          </svg>
        </button>
      `;
      card.querySelector('[data-mw-select-photo]')?.addEventListener('click', () => this.selectUploadedPhoto(photo));
      card.querySelector('[data-mw-delete-photo]')?.addEventListener('click', () => this.deleteUploadedPhoto(photo));
      this.photoGrid.appendChild(card);
    });

    this.updatePhotoNavState();
  }

  scrollPhotoLibrary(direction) {
    const windowSize = this.getPhotoWindowSize();
    const maxStart = Math.max(0, this.photos.length - windowSize);
    this.photoPageStart = Math.min(maxStart, Math.max(0, this.photoPageStart + direction * windowSize));
    this.renderPhotoWindow();
  }

  updatePhotoNavState() {
    if (!this.photoPrev || !this.photoNext) return;

    const windowSize = this.getPhotoWindowSize();
    const maxStart = Math.max(0, this.photos.length - windowSize);
    const hasOverflow = this.photos.length > windowSize;
    this.photoPrev.hidden = !hasOverflow;
    this.photoNext.hidden = !hasOverflow;

    if (!hasOverflow) return;
    this.photoPrev.disabled = this.photoPageStart <= 0;
    this.photoNext.disabled = this.photoPageStart >= maxStart;
  }

  async loadGeneratedPreviewLibrary(page = this.generatedPreviewPage || 1) {
    if (!this.generatedLibrary || !this.generatedGrid) return;

    const nextPage = Math.max(1, Number(page) || 1);

    try {
      const data = await this.proxyFetch('/images/list', {
        body: JSON.stringify({
          profession: this.profession,
          size: this.size,
          page: nextPage,
          page_size: GENERATED_PREVIEW_PAGE_SIZE,
        }),
      });
      this.loginRequired = false;
      this.hideLoginAlert();
      this.generatedPreviewPage = nextPage;
      this.renderGeneratedPreviewLibrary(data);
    } catch (error) {
      if (this.isLoginRequiredStatus(error.status, error.data)) {
        this.showLoginRequired();
        return;
      }
      this.generatedLibrary.hidden = true;
    }
  }

  normalizeGeneratedPreviewList(data) {
    const items = data?.images || data?.previews || data?.results || data?.items || data?.data || [];
    return Array.isArray(items) ? items : [];
  }

  renderGeneratedPreviewLibrary(data) {
    if (!this.generatedLibrary || !this.generatedGrid) return;

    this.generatedPreviews = this.normalizeGeneratedPreviewList(data).filter((item) => this.getGeneratedPreviewUrl(item));
    const previousTotal = this.generatedPreviewTotal;
    this.generatedPreviewTotal = Number(data?.total ?? data?.count ?? this.generatedPreviews.length) || this.generatedPreviews.length;

    if (!this.generatedPreviews.length) {
      if (this.generatedPreviewPage > 1 && (this.generatedPreviewTotal > 0 || previousTotal > 0)) {
        this.loadGeneratedPreviewLibrary(this.generatedPreviewPage - 1);
        return;
      }

      this.generatedGrid.innerHTML = '';
      this.generatedLibrary.hidden = true;
      return;
    }

    this.generatedLibrary.hidden = false;
    this.generatedGrid.innerHTML = '';

    this.generatedPreviews.forEach((preview) => {
      const jobId = this.getGeneratedJobId(preview);
      const previewUrl = this.getGeneratedPreviewUrl(preview);
      const card = document.createElement('div');
      card.className = 'mw-personalized__photo-card mw-personalized__generated-card';
      card.dataset.jobId = String(jobId || '');
      card.classList.toggle('is-selected', String(this.getGeneratedJobId(this.generatedModel)) === String(jobId));
      card.innerHTML = `
        <button type="button" class="mw-personalized__photo-select" data-mw-select-generated>
          <img src="${this.escapeAttribute(previewUrl)}" alt="Saved generated miniature preview" loading="lazy">
          <span>Generated preview</span>
        </button>
        <button type="button" class="mw-personalized__photo-delete" data-mw-delete-generated aria-label="Delete generated preview">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M9 3h6l1 2h4v2H4V5h4l1-2Z"></path>
            <path d="M6 9h12l-.7 11H6.7L6 9Zm4 2v7h1.6v-7H10Zm2.4 0v7H14v-7h-1.6Z"></path>
          </svg>
        </button>
      `;
      card.querySelector('[data-mw-select-generated]')?.addEventListener('click', () => this.selectGeneratedPreview(preview));
      card.querySelector('[data-mw-delete-generated]')?.addEventListener('click', () => this.deleteGeneratedPreview(preview));
      this.generatedGrid.appendChild(card);
    });

    this.updateGeneratedPreviewNavState();
  }

  updateGeneratedPreviewNavState() {
    if (!this.generatedPrev || !this.generatedNext) return;

    const pageSize = GENERATED_PREVIEW_PAGE_SIZE;
    const totalPages = Math.max(1, Math.ceil(this.generatedPreviewTotal / pageSize));
    const hasOverflow = totalPages > 1;
    this.generatedPrev.hidden = !hasOverflow;
    this.generatedNext.hidden = !hasOverflow;

    if (this.generatedCount) {
      const start = (this.generatedPreviewPage - 1) * pageSize + 1;
      const end = Math.min(start + this.generatedPreviews.length - 1, this.generatedPreviewTotal);
      this.generatedCount.textContent = hasOverflow ? `${start}-${end} of ${this.generatedPreviewTotal}` : `${this.generatedPreviewTotal} saved`;
    }

    if (!hasOverflow) return;
    this.generatedPrev.disabled = this.generatedPreviewPage <= 1;
    this.generatedNext.disabled = this.generatedPreviewPage >= totalPages;
  }

  selectGeneratedPreview(preview) {
    const jobId = this.getGeneratedJobId(preview);
    const previewUrl = this.getGeneratedPreviewUrl(preview);
    const photoId = this.getGeneratedPhotoId(preview);

    if (!jobId || !previewUrl) {
      this.showError('This generated preview cannot be selected. Please generate a new preview.');
      return;
    }

    this.generatedModel = {
      ...preview,
      job_id: jobId,
      name: this.getGeneratedPersonName(preview),
      quote: this.getGeneratedQuote(preview),
      status: preview.status || 'completed',
    };

    if (photoId) {
      this.selectedPhoto = { ...(this.selectedPhoto || {}), id: photoId };
      if (this.photoProperty) this.photoProperty.value = photoId;
    }

    if (this.jobProperty) this.jobProperty.value = jobId;
    this.restoreGeneratedTextFields(this.generatedModel);
    this.renderGeneratedModel(this.generatedModel);
    this.hideError();

    this.generatedGrid?.querySelectorAll('.mw-personalized__generated-card').forEach((card) => {
      card.classList.toggle('is-selected', card.dataset.jobId === String(jobId));
    });
  }

  async deleteGeneratedPreview(preview) {
    const jobId = this.getGeneratedJobId(preview);
    if (!jobId || this.deletingGeneratedJobIds.has(String(jobId))) return;

    const confirmed = window.confirm('Delete this generated preview? This cannot be undone.');
    if (!confirmed) return;

    this.deletingGeneratedJobIds.add(String(jobId));
    this.setGeneratedPreviewDeletingState(jobId, true);
    this.hideError();

    try {
      await this.proxyFetch('/images/delete', {
        body: JSON.stringify({ job_id: Number(jobId) }),
      });

      const nextPage =
        this.generatedPreviews.length <= 1 && this.generatedPreviewPage > 1
          ? this.generatedPreviewPage - 1
          : this.generatedPreviewPage;

      if (String(this.getGeneratedJobId(this.generatedModel)) === String(jobId)) {
        this.generatedModel = null;
        if (this.jobProperty) this.jobProperty.value = '';
        if (this.generatedViewer) {
          this.generatedViewer.hidden = true;
          this.generatedViewer.innerHTML = '';
        }
      }

      await this.loadGeneratedPreviewLibrary(nextPage);
    } catch (error) {
      if (this.isServiceUnavailableError(error)) {
        this.setServiceUnavailable(SERVICE_UNAVAILABLE_MESSAGE);
      } else {
        this.showError(error.message || 'Generated preview delete failed. Please try again.');
      }
    } finally {
      this.deletingGeneratedJobIds.delete(String(jobId));
      this.setGeneratedPreviewDeletingState(jobId, false);
    }
  }

  setGeneratedPreviewDeletingState(jobId, isDeleting) {
    const card = Array.from(this.generatedGrid?.querySelectorAll('.mw-personalized__generated-card') || []).find(
      (item) => item.dataset.jobId === String(jobId)
    );
    if (!card) return;

    card.classList.toggle('is-deleting', isDeleting);
    card.querySelectorAll('button').forEach((button) => {
      button.disabled = isDeleting;
      button.toggleAttribute('aria-disabled', isDeleting);
    });
  }
  handleDrop(event) {
    event.preventDefault();
    this.dropzone?.classList.remove('is-dragging');
    const file = event.dataTransfer?.files?.[0];
    if (!file || !this.input) return;

    const transfer = new DataTransfer();
    transfer.items.add(file);
    this.input.files = transfer.files;
    this.handleFileSelection();
  }

  handleFileSelection() {
    if (!this.validateSelectedFile()) return;
    window.setTimeout(() => this.uploadSelectedPhoto({ skipValidation: true }), 0);
  }

  validateSelectedFile() {
    const file = this.input?.files?.[0];
    this.hideError();
    this.hideUploadError();
    this.clearSelectedPhotoState();

    if (!file) {
      this.clearPreview();
      this.disableGenerateButton();
      return false;
    }

    if (!VALID_TYPES.has(file.type)) {
      this.clearFile(false);
      this.showUploadError('Please choose a JPG, PNG or WEBP image.');
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      this.clearFile(false);
      this.showUploadError(`The selected photo is larger than ${MAX_FILE_SIZE_MB} MB. Please choose a smaller file.`);
      return false;
    }

    this.showPreview(file);
    this.disableGenerateButton();
    this.setUploadButtonState(false, 'Upload photo');
    return true;
  }

  showPreview(file) {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = URL.createObjectURL(file);
    if (this.preview) this.preview.src = this.previewUrl;
    if (this.fileName) this.fileName.textContent = file.name;
    if (this.fileStatus) this.fileStatus.textContent = 'Photo selected. Uploading now...';
    if (this.uploadButton) this.uploadButton.hidden = true;
    if (this.selection) this.selection.hidden = false;
    if (this.dropzone) this.dropzone.hidden = true;
  }

  async uploadSelectedPhoto(options = {}) {
    if (this.isUploading) return;
    if (this.loginRequired) {
      this.showSignInPrompt();
      return;
    }

    const file = this.input?.files?.[0];
    if ((!options.skipValidation && !this.validateSelectedFile()) || !file) return;

    const personName = this.getPersonNameValue();
    this.isUploading = true;
    this.setUploadButtonState(true, 'Uploading...');
    if (this.fileStatus) this.fileStatus.textContent = 'Uploading photo...';
    this.disableGenerateButton();

    try {
      const uploadData = await this.proxyFetch('/photos/upload-url', {
        body: JSON.stringify({
          person_name: personName,
          description: `${this.profession} miniature photo`,
          content_type: file.type,
        }),
      });

      if (!uploadData?.upload_url || !uploadData?.file_key) {
        throw Object.assign(new Error(SERVICE_UNAVAILABLE_MESSAGE), { status: 503 });
      }

      const uploadHeaders = {
        'Content-Type': file.type,
        ...(uploadData?.instructions?.headers || {}),
      };

      const signedHeaders = new URL(uploadData.upload_url).searchParams.get('X-Amz-SignedHeaders') || '';
      if (signedHeaders.includes('x-amz-acl') && !uploadHeaders['x-amz-acl']) {
        uploadHeaders['x-amz-acl'] = 'private';
      }

      const uploadResponse = await fetch(uploadData.upload_url, {
        method: 'PUT',
        headers: uploadHeaders,
        body: file,
      });

      if (!uploadResponse.ok) throw new Error('Photo upload failed. Please try again.');

      const photo = await this.proxyFetch('/photos/confirm', {
        body: JSON.stringify({
          file_key: uploadData.file_key,
          person_name: personName,
          description: `${this.profession} miniature photo`,
          content_type: file.type,
        }),
      });

      this.selectUploadedPhoto(photo);
      this.resetUploadPicker();
      this.addConfirmedPhotoToLibrary(photo);
      this.loadPhotoLibrary();
      this.loadGeneratedPreviewLibrary();
    } catch (error) {
      if (this.isServiceUnavailableError(error)) {
        this.setServiceUnavailable(SERVICE_UNAVAILABLE_MESSAGE);
      } else {
        if (this.fileStatus) this.fileStatus.textContent = 'Upload failed. Choose another photo or try again.';
        if (this.uploadButton) this.uploadButton.hidden = false;
        this.showError(error.message || 'Photo upload failed. Please try again.');
        this.setUploadButtonState(false, 'Try upload again');
      }
    } finally {
      this.isUploading = false;
    }
  }

  selectUploadedPhoto(photo) {
    this.selectedPhoto = photo;
    this.generatedModel = null;
    this.deletingGeneratedJobIds = new Set();

    if (this.photoProperty) this.photoProperty.value = photo.id || '';
    if (this.jobProperty) this.jobProperty.value = '';
    if (this.generatedViewer) {
      this.generatedViewer.hidden = true;
      this.generatedViewer.innerHTML = '';
    }

    this.photoGrid?.querySelectorAll('.mw-personalized__photo-card').forEach((card) => {
      card.classList.toggle('is-selected', card.dataset.photoId === String(photo.id));
    });

    this.enableGenerateButton();
    this.hideError();
  }

  addConfirmedPhotoToLibrary(photo) {
    if (!photo?.id) return;
    const existing = this.photos.filter((item) => String(item.id) !== String(photo.id));
    this.photoPageStart = 0;
    this.renderPhotoLibrary([photo, ...existing]);
    this.selectUploadedPhoto(photo);
  }

  async deleteUploadedPhoto(photo) {
    if (!photo?.id || this.deletingPhotoIds.has(String(photo.id))) return;

    const confirmed = window.confirm('Delete this uploaded photo? This cannot be undone.');
    if (!confirmed) return;

    const photoId = String(photo.id);
    this.deletingPhotoIds.add(photoId);
    this.setPhotoDeletingState(photoId, true);
    this.hideError();

    try {
      await this.proxyFetch('/photos/delete', {
        body: JSON.stringify({ photo_id: Number(photo.id) }),
      });

      const wasSelected = String(this.selectedPhoto?.id) === photoId;
      this.photos = this.photos.filter((item) => String(item.id) !== photoId);

      if (wasSelected) {
        this.clearSelectedPhotoState();
        this.disableGenerateButton();
        if (this.generatedViewer) {
          this.generatedViewer.hidden = true;
          this.generatedViewer.innerHTML = '';
        }
      }

      this.photoPageStart = Math.min(this.photoPageStart, Math.max(0, this.photos.length - this.getPhotoWindowSize()));
      this.renderPhotoLibrary(this.photos);
    } catch (error) {
      if (this.isServiceUnavailableError(error)) {
        this.setServiceUnavailable(SERVICE_UNAVAILABLE_MESSAGE);
      } else {
        this.showError(error.message || 'Photo delete failed. Please try again.');
      }
    } finally {
      this.deletingPhotoIds.delete(photoId);
      this.setPhotoDeletingState(photoId, false);
    }
  }

  setPhotoDeletingState(photoId, isDeleting) {
    const card = Array.from(this.photoGrid?.querySelectorAll('.mw-personalized__photo-card') || []).find(
      (item) => item.dataset.photoId === String(photoId)
    );
    if (!card) return;

    card.classList.toggle('is-deleting', isDeleting);
    card.querySelectorAll('button').forEach((button) => {
      button.disabled = isDeleting;
      button.toggleAttribute('aria-disabled', isDeleting);
    });
  }

  async generateModel() {
    if (this.loginRequired) {
      this.showSignInPrompt();
      return;
    }

    if (this.isGenerating || !this.selectedPhoto?.id) {
      this.showError('Upload or select a photo before generating the 3D model.');
      return;
    }

    if (!this.validateRequiredPersonName()) return;

    this.isGenerating = true;
    this.updateGenerationProgress(1, 'Starting');

    try {
      if (!ALLOWED_PROFESSIONS.has(this.profession) || !ALLOWED_SIZES.has(this.size)) {
        const setupError = new Error('This product needs a supported profession and size before preview generation can start.');
        setupError.status = 400;
        throw setupError;
      }

      const generationPayload = {
        source_photo_id: Number(this.selectedPhoto.id),
        profession: this.profession,
        size: this.size,
      };
      const personName = this.personNameInput?.value?.trim().slice(0, PERSON_NAME_MAX_LENGTH);
      const miniatureQuote = this.quoteInput?.value?.trim().slice(0, MINIATURE_QUOTE_MAX_LENGTH);

      if (personName) generationPayload.name = personName;
      if (miniatureQuote) generationPayload.quote = miniatureQuote;

      const job = await this.proxyFetch('/images/generate', {
        body: JSON.stringify(generationPayload),
      });

      if (this.jobProperty) this.jobProperty.value = job.job_id || '';
      const completedJob = await this.pollModelStatus(job.job_id);
      const result = await this.proxyFetch('/images/result', {
        body: JSON.stringify({ job_id: Number(completedJob.id || job.job_id) }),
      });

      const previewUrl = this.getGeneratedPreviewUrl(result);
      if (result.status !== 'completed' || !previewUrl) {
        throw new Error(this.getCustomerSafeErrorMessage(result, 'The generated preview is not ready yet. Please try again shortly.'));
      }

      this.generatedModel = {
        ...result,
        job_id: result.job_id || completedJob.id || job.job_id,
        source_photo_id: result.source_photo_id || result.photo_id || this.selectedPhoto.id,
        name: this.getGeneratedPersonName(result) || personName || '',
        quote: this.getGeneratedQuote(result) || miniatureQuote || '',
        status: result.status || 'completed',
      };
      this.renderGeneratedModel(this.generatedModel);
      this.generatedPreviewPage = 1;
      this.loadGeneratedPreviewLibrary(1);
      this.hideGenerationProgress();
      this.setGenerateState(false, GENERATE_BUTTON_LABEL);
      this.hideError();
    } catch (error) {
      this.hideGenerationProgress();
      if (this.isServiceUnavailableError(error)) {
        this.setServiceUnavailable(SERVICE_UNAVAILABLE_MESSAGE);
      } else {
        this.showError(this.getCustomerSafeErrorMessage(error, 'Preview generation failed. Please try again.'));
        this.setGenerateState(false, GENERATE_BUTTON_LABEL);
      }
    } finally {
      this.isGenerating = false;
    }
  }

  async pollModelStatus(jobId) {
    let visibleProgress = 1;
    let visibleStatus = 'Generating';
    const updateVisibleProgress = () => {
      this.updateGenerationProgress(visibleProgress, visibleStatus);
    };

    this.stopGenerationProgressTimer();
    updateVisibleProgress();
    this.generationProgressTimer = window.setInterval(() => {
      if (visibleProgress >= 99) return;
      visibleProgress = Math.min(99, visibleProgress + 1);
      updateVisibleProgress();
    }, LOCAL_PROGRESS_INTERVAL);

    try {
      for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
        const job = await this.proxyFetch('/images/status', {
          body: JSON.stringify({ job_id: Number(jobId) }),
        });

        if (job.status === 'completed') return job;
        if (job.status === 'failed') {
          throw new Error(this.getCustomerSafeErrorMessage(job, 'Preview generation failed.'));
        }

        visibleStatus = job.status === 'queued' || job.status === 'pending' ? 'Queued' : 'Generating';
        const backendProgress = this.getBackendGenerationProgress(job);
        if (backendProgress !== null) {
          visibleProgress = Math.max(visibleProgress, backendProgress);
        }
        updateVisibleProgress();
        await sleep(POLL_INTERVAL);
      }
    } finally {
      this.stopGenerationProgressTimer();
    }

    throw new Error('The generated preview is taking longer than expected. Please try again in a few minutes.');
  }

  getBackendGenerationProgress(job) {
    const rawProgress =
      job?.progress_percent ??
      job?.progress_percentage ??
      job?.progress ??
      job?.percent ??
      job?.percentage ??
      job?.data?.progress;

    const numericProgress = Number(rawProgress);
    if (Number.isFinite(numericProgress)) {
      const normalizedProgress = numericProgress <= 1 ? numericProgress * 100 : numericProgress;
      return Math.min(99, Math.max(1, Math.round(normalizedProgress)));
    }

    return null;
  }

  stopGenerationProgressTimer() {
    if (!this.generationProgressTimer) return;
    window.clearInterval(this.generationProgressTimer);
    this.generationProgressTimer = null;
  }

  updateGenerationProgress(progress, status = 'Generating') {
    const safeProgress = Math.min(99, Math.max(1, Math.round(Number(progress) || 1)));
    this.setGenerateState(true, `${status} ${safeProgress}%`);
    if (this.generationProgress) this.generationProgress.hidden = false;
    if (this.generationProgressBar) this.generationProgressBar.style.width = `${safeProgress}%`;
    if (this.generationProgressText) this.generationProgressText.textContent = `${safeProgress}%`;
  }

  hideGenerationProgress() {
    this.stopGenerationProgressTimer();
    if (this.generationProgress) this.generationProgress.hidden = true;
    if (this.generationProgressBar) this.generationProgressBar.style.width = '1%';
    if (this.generationProgressText) this.generationProgressText.textContent = '1%';
  }

  getGeneratedPreviewUrl(result) {
    return result?.result_url || result?.image_url || result?.preview_url || result?.download_url || result?.url || '';
  }

  getGeneratedJobId(result) {
    return result?.job_id || result?.id || result?.model_job_id || result?.image_job_id || '';
  }

  getGeneratedPhotoId(result) {
    return result?.source_photo_id || result?.photo_id || result?.customer_photo_id || result?.photo?.id || '';
  }

  renderGeneratedModel(result) {
    if (!this.generatedViewer) return;

    const previewUrl = this.getGeneratedPreviewUrl(result);

    this.generatedViewer.hidden = false;
    this.generatedViewer.innerHTML = `
      <div class="mw-personalized__model-success">
        <strong>Preview ready</strong>
        <span>Your generated preview is attached to this order. Review it before continuing.</span>
      </div>
      <div class="mw-personalized__generated-image-wrap">
        <img
          class="mw-personalized__generated-image"
          src="${this.escapeAttribute(previewUrl)}"
          alt="Generated miniature preview"
          loading="eager"
        >
        <button type="button" class="mw-personalized__fullscreen-button" data-mw-preview-fullscreen aria-label="View generated preview fullscreen">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M4 4h7v2H7.4l4.3 4.3-1.4 1.4L6 7.4V11H4V4Zm9 0h7v7h-2V7.4l-4.3 4.3-1.4-1.4L16.6 6H13V4ZM4 13h2v3.6l4.3-4.3 1.4 1.4L7.4 18H11v2H4v-7Zm14 0h2v7h-7v-2h3.6l-4.3-4.3 1.4-1.4 4.3 4.3V13Z"></path>
          </svg>
        </button>
        <button type="button" class="mw-personalized__fullscreen-close" data-mw-preview-exit-fullscreen aria-label="Exit fullscreen preview">
          &times;
        </button>
      </div>
    `;

    const wrapper = this.generatedViewer.querySelector('.mw-personalized__generated-image-wrap');
    const fullscreenButton = this.generatedViewer.querySelector('[data-mw-preview-fullscreen]');
    const exitFullscreenButton = this.generatedViewer.querySelector('[data-mw-preview-exit-fullscreen]');
    fullscreenButton?.addEventListener('click', () => this.openGeneratedPreviewFullscreen(previewUrl, wrapper));
    exitFullscreenButton?.addEventListener('click', () => {
      if (document.fullscreenElement) document.exitFullscreen();
    });
  }

  async openGeneratedPreviewFullscreen(previewUrl, wrapper) {
    if (!previewUrl) return;

    if (wrapper?.requestFullscreen && document.fullscreenEnabled) {
      try {
        await wrapper.requestFullscreen();
        return;
      } catch {
        // Use the modal fallback below when the browser refuses fullscreen.
      }
    }

    this.openGeneratedPreviewModal(previewUrl);
  }

  openGeneratedPreviewModal(previewUrl) {
    const existingModal = document.querySelector('[data-mw-generated-preview-modal]');
    existingModal?.remove();

    const modal = document.createElement('div');
    modal.className = 'mw-personalized__preview-modal';
    modal.dataset.mwGeneratedPreviewModal = 'true';
    modal.innerHTML = `
      <button type="button" class="mw-personalized__preview-modal-close" aria-label="Close generated preview">
        &times;
      </button>
      <img src="${this.escapeAttribute(previewUrl)}" alt="Generated miniature preview fullscreen">
    `;

    const closeModal = () => {
      modal.remove();
      document.removeEventListener('keydown', handleKeydown);
    };

    const handleKeydown = (event) => {
      if (event.key === 'Escape') closeModal();
    };

    modal.addEventListener('click', (event) => {
      if (event.target === modal) closeModal();
    });
    modal.querySelector('button')?.addEventListener('click', closeModal);
    document.addEventListener('keydown', handleKeydown);
    document.body.appendChild(modal);
  }

  escapeAttribute(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  parseJsonDataset(value, fallback) {
    if (!value) return fallback;

    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }

  getSelectedVariantId() {
    const formVariantId = this.form?.querySelector('[name="id"]')?.value?.trim();
    if (formVariantId) return formVariantId;

    const checkedVariantOption = document.querySelector('variant-picker input:checked[data-variant-id]');
    const checkedVariantId = checkedVariantOption?.dataset?.variantId?.trim();
    if (checkedVariantId) return checkedVariantId;

    const selectedVariantOption = document.querySelector('variant-picker [data-current-checked="true"][data-variant-id]');
    return selectedVariantOption?.dataset?.variantId?.trim() || '';
  }

  getCurrentOrderPrice() {
    const selectedVariantId = this.getSelectedVariantId();
    const mappedPrice = selectedVariantId ? this.variantPrices?.[selectedVariantId] : '';

    return mappedPrice || this.productPrice || 'Shown at checkout';
  }

  clearPreview() {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = '';
    this.preview?.removeAttribute('src');
    if (this.fileName) this.fileName.textContent = '';
    if (this.fileStatus) this.fileStatus.textContent = '';
    if (this.selection) this.selection.hidden = true;
    if (this.dropzone) this.dropzone.hidden = false;
  }

  resetUploadPicker() {
    if (this.input) this.input.value = '';
    this.clearPreview();
    this.hideUploadError();
    if (this.uploadButton) this.uploadButton.hidden = true;
    this.setUploadButtonState(false, 'Upload photo');
  }

  clearFile(hideError = true) {
    if (this.input) this.input.value = '';
    this.clearPreview();
    this.clearSelectedPhotoState();
    this.disableGenerateButton();
    if (this.uploadButton) this.uploadButton.hidden = true;
    this.setUploadButtonState(false, 'Upload photo');
    if (hideError) {
      this.hideError();
      this.hideUploadError();
    }
  }

  clearSelectedPhotoState() {
    this.selectedPhoto = null;
    this.generatedModel = null;
    this.generatedPreviews = [];
    this.generatedPreviewPage = 1;
    this.generatedPreviewTotal = 0;
    this.deletingGeneratedJobIds = new Set();
    if (this.photoProperty) this.photoProperty.value = '';
    if (this.jobProperty) this.jobProperty.value = '';
  }

  hasConfirmedModel() {
    return Boolean(this.jobProperty?.value && this.generatedModel?.status === 'completed');
  }

  handlePurchaseClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const stickyButton = target.closest('sticky-add-to-cart [ref="addToCartButton"]');
    const formButton = target.closest(
      'button[name="add"], .shopify-payment-button__button, [data-shopify-buttoncontainer] button, shopify-accelerated-checkout'
    );
    const belongsToForm = formButton && (formButton.closest('form') === this.form || this.form?.contains(formButton));

    if (!stickyButton && !belongsToForm) return;
    this.guardPurchase(event, stickyButton || formButton);
  }

  guardPurchase(event, purchaseTrigger = null) {
    if (!this.serviceAvailable) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showError(this.dataset.unavailableMessage || SERVICE_UNAVAILABLE_MESSAGE);
      return false;
    }

    if (this.loginRequired) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.hideError();
      this.showSignInPrompt();
      return false;
    }

    if (!this.validateRequiredPersonName()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return false;
    }

    if (this.hasConfirmedModel()) {
      if (!this.isPurchaseConfirmationBypassActive()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.hideError();
        this.showGeneratedOrderConfirmation(purchaseTrigger);
        return false;
      }

      this.prepareOptionalOrderProperties();
      this.hideError();
      return true;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    this.hideError();
    this.showPreviewRequiredPrompt();
    return false;
  }

  isPurchaseConfirmationBypassActive() {
    return Date.now() < this.purchaseConfirmationBypassUntil;
  }

  showPreviewRequiredPrompt() {
    const existingModal = document.querySelector('[data-mw-preview-required-modal]');
    existingModal?.remove();

    const hasSelectedPhoto = Boolean(this.selectedPhoto?.id);
    const primaryLabel = hasSelectedPhoto ? 'Generate preview' : 'Choose photo';

    const modal = document.createElement('div');
    modal.className = 'mw-personalized__login-modal mw-personalized__preview-required-modal';
    modal.dataset.mwPreviewRequiredModal = 'true';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'MwPreviewRequiredTitle');
    modal.innerHTML = `
      <div class="mw-personalized__login-modal-card">
        <button type="button" class="mw-personalized__login-modal-close" aria-label="Close preview requirement">&times;</button>
        <p class="mw-personalized__login-modal-eyebrow">Preview required</p>
        <h3 id="MwPreviewRequiredTitle">Create your preview before ordering</h3>
        <p>Your custom miniature is made from the generated preview. Please complete these steps before checkout.</p>
        <ol class="mw-personalized__preview-required-steps">
          <li><span>1</span>Upload or select your photo</li>
          <li><span>2</span>Generate the preview</li>
          <li><span>3</span>Review and confirm it</li>
        </ol>
        <div class="mw-personalized__preview-required-actions">
          <button type="button" class="mw-personalized__login-modal-button" data-mw-preview-required-primary>${primaryLabel}</button>
          <button type="button" class="mw-personalized__preview-required-secondary">Continue editing</button>
        </div>
      </div>
    `;

    const focusNextStep = () => {
      const target = hasSelectedPhoto ? this.generateButton : this.dropzone;
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target?.focus?.({ preventScroll: true });
    };

    const closeModal = () => {
      modal.remove();
      document.removeEventListener('keydown', handleKeydown);
    };

    const handleKeydown = (event) => {
      if (event.key === 'Escape') closeModal();
    };

    modal.addEventListener('click', (event) => {
      if (event.target === modal) closeModal();
    });
    modal.querySelector('.mw-personalized__login-modal-close')?.addEventListener('click', closeModal);
    modal.querySelector('.mw-personalized__preview-required-secondary')?.addEventListener('click', closeModal);
    modal.querySelector('[data-mw-preview-required-primary]')?.addEventListener('click', () => {
      closeModal();
      focusNextStep();
    });
    document.addEventListener('keydown', handleKeydown);
    document.body.appendChild(modal);
    modal.querySelector('[data-mw-preview-required-primary]')?.focus();
  }

  showGeneratedOrderConfirmation(purchaseTrigger = null) {
    const previewUrl = this.getGeneratedPreviewUrl(this.generatedModel);
    if (!previewUrl) {
      this.showError('Generate and review your preview before placing this custom order.');
      return;
    }

    const existingModal = document.querySelector('[data-mw-generated-order-confirmation]');
    existingModal?.remove();

    const personName = this.getOrderPersonName();
    const miniatureQuote = this.getOrderQuote();
    const confirmationDetails = [
      ['Miniature Style', this.miniatureStyle],
      ['Size', this.getSelectedMiniatureDisplaySize()],
      ['Name', personName || 'Not added'],
      ['Price', this.getCurrentOrderPrice()],
    ];
    if (this.quoteEnabled) {
      confirmationDetails.splice(3, 0, ['Quote/Date', miniatureQuote || 'Not added']);
    }
    const modal = document.createElement('div');
    modal.className = 'mw-personalized__confirm-modal';
    modal.dataset.mwGeneratedOrderConfirmation = 'true';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'MwGeneratedOrderConfirmTitle');
    modal.innerHTML = `
      <div class="mw-personalized__confirm-card">
        <button type="button" class="mw-personalized__confirm-close" aria-label="Close preview confirmation">&times;</button>
        <p class="mw-personalized__confirm-eyebrow">Review before order</p>
        <h3 id="MwGeneratedOrderConfirmTitle">Confirm your generated preview</h3>
        <p class="mw-personalized__confirm-copy">This is the preview attached to your custom miniature order. Please review it carefully before continuing.</p>
        <img class="mw-personalized__confirm-image" src="${this.escapeAttribute(previewUrl)}" alt="Generated miniature preview for order confirmation">
        <dl class="mw-personalized__confirm-details">
          ${confirmationDetails
            .map(([label, value]) => `<div><dt>${this.escapeAttribute(label)}</dt><dd>${this.escapeAttribute(value)}</dd></div>`)
            .join('')}
        </dl>
        <div class="mw-personalized__confirm-actions">
          <button type="button" class="mw-personalized__confirm-secondary">Review again</button>
          <button type="button" class="mw-personalized__confirm-primary">Confirm & Continue</button>
        </div>
      </div>
    `;

    const closeModal = () => {
      modal.remove();
      document.removeEventListener('keydown', handleKeydown);
    };

    const confirmAndContinue = () => {
      this.purchaseConfirmationBypassUntil = Date.now() + 3500;
      this.prepareOptionalOrderProperties();
      closeModal();
      window.setTimeout(() => {
        if (purchaseTrigger && typeof purchaseTrigger.click === 'function') {
          purchaseTrigger.click();
        } else if (this.form?.requestSubmit) {
          this.form.requestSubmit();
        } else {
          this.form?.submit();
        }
      }, 0);
    };

    const handleKeydown = (event) => {
      if (event.key === 'Escape') closeModal();
    };

    modal.addEventListener('click', (event) => {
      if (event.target === modal) closeModal();
    });
    modal.querySelector('.mw-personalized__confirm-close')?.addEventListener('click', closeModal);
    modal.querySelector('.mw-personalized__confirm-secondary')?.addEventListener('click', closeModal);
    modal.querySelector('.mw-personalized__confirm-primary')?.addEventListener('click', confirmAndContinue);
    document.addEventListener('keydown', handleKeydown);
    document.body.appendChild(modal);
    modal.querySelector('.mw-personalized__confirm-primary')?.focus();
  }

  getSelectedMiniatureDisplaySize() {
    const picker = document.querySelector('variant-picker[data-template-product-match="true"]') || document.querySelector('variant-picker');

    if (picker) {
      const selectedFieldsetSize = Array.from(picker.querySelectorAll('fieldset.variant-option')).find((fieldset) => {
        const legend = fieldset.querySelector('legend')?.textContent?.trim().toLowerCase() || '';
        return legend.includes('size') || legend.includes('inch') || legend.includes('height');
      });
      const selectedFieldsetValue = selectedFieldsetSize?.querySelector('input:checked')?.value?.trim();
      if (selectedFieldsetValue) return selectedFieldsetValue;

      const selectedDropdownSize = Array.from(picker.querySelectorAll('.variant-option--dropdowns')).find((wrapper) => {
        const label = wrapper.querySelector('label')?.textContent?.trim().toLowerCase() || '';
        return label.includes('size') || label.includes('inch') || label.includes('height');
      });
      const selectedDropdownValue = selectedDropdownSize?.querySelector('select')?.selectedOptions?.[0]?.textContent?.trim();
      if (selectedDropdownValue) return selectedDropdownValue.replace(/\s+-\s+Unavailable$/i, '').trim();

      const selectedValues = Array.from(picker.querySelectorAll('input:checked, select'))
        .map((control) => {
          if (control.tagName === 'SELECT') return control.selectedOptions?.[0]?.textContent?.trim() || '';
          return control.value?.trim() || '';
        })
        .filter(Boolean);
      const inchValue = selectedValues.find((value) => /\b\d+(\.\d+)?\s*(inch|inches|in|")\b/i.test(value));
      if (inchValue) return inchValue;
    }

    return this.miniatureDisplaySize || 'Selected size';
  }

  getGeneratedPersonName(result = this.generatedModel) {
    const rawName =
      result?.name ??
      result?.person_name ??
      result?.personName ??
      result?.customer_name ??
      result?.metadata?.name ??
      result?.metadata?.person_name ??
      '';

    return String(rawName || '').trim().slice(0, PERSON_NAME_MAX_LENGTH);
  }

  getGeneratedQuote(result = this.generatedModel) {
    if (!this.quoteEnabled) return '';

    const rawQuote =
      result?.quote ??
      result?.miniature_quote ??
      result?.miniatureQuote ??
      result?.quote_date ??
      result?.quoteDate ??
      result?.metadata?.quote ??
      result?.metadata?.miniature_quote ??
      '';

    return String(rawQuote || '').trim().slice(0, MINIATURE_QUOTE_MAX_LENGTH);
  }

  getOrderPersonName() {
    return this.getGeneratedPersonName() || this.personNameInput?.value?.trim().slice(0, PERSON_NAME_MAX_LENGTH) || '';
  }

  validateRequiredPersonName() {
    if (!this.personNameRequired) return true;

    const personName = this.personNameInput?.value?.trim() || '';
    if (personName) return true;

    this.showError('Enter the person name before generating or ordering this miniature.');
    this.personNameInput?.focus();
    return false;
  }

  getOrderQuote() {
    if (!this.quoteEnabled) return '';

    return this.getGeneratedQuote() || this.quoteInput?.value?.trim().slice(0, MINIATURE_QUOTE_MAX_LENGTH) || '';
  }

  restoreGeneratedTextFields(result = this.generatedModel) {
    if (this.personNameInput) {
      this.personNameInput.value = this.getGeneratedPersonName(result);
      this.updateCharacterLimitMessage(this.personNameInput, this.personNameLimit, PERSON_NAME_MAX_LENGTH);
    }

    if (this.quoteInput) {
      this.quoteInput.value = this.getGeneratedQuote(result);
      this.updateCharacterLimitMessage(this.quoteInput, this.quoteLimit, MINIATURE_QUOTE_MAX_LENGTH);
    }
  }

  invalidateGeneratedPreviewForDetailChange() {
    if (!this.generatedModel) return;

    this.generatedModel = null;
    if (this.jobProperty) this.jobProperty.value = '';
    if (this.generatedViewer) {
      this.generatedViewer.hidden = true;
      this.generatedViewer.innerHTML = '';
    }
    this.generatedGrid?.querySelectorAll('.mw-personalized__generated-card').forEach((card) => {
      card.classList.remove('is-selected');
    });
    this.setGenerateState(false, GENERATE_BUTTON_LABEL);
    this.showError('You changed the name or quote. Please generate a new preview so your order matches your details.');
  }

  getPersonNameValue() {
    return this.personNameInput?.value?.trim().slice(0, PERSON_NAME_MAX_LENGTH) || 'none';
  }

  prepareOptionalOrderProperties() {
    if (this.personNameInput) {
      const personName = this.getOrderPersonName();
      this.personNameInput.value = personName;
    }

    if (this.quoteInput) {
      this.quoteInput.value = this.getOrderQuote();
    }
  }

  updateCharacterLimitMessage(input, message, maxLength) {
    if (!input || !message) return;
    message.hidden = input.value.length < maxLength;
  }

  enableGenerateButton() {
    if (!this.generateButton || !this.serviceAvailable || !this.selectedPhoto?.id) return;
    this.generateButton.disabled = false;
    this.generateButton.textContent = GENERATE_BUTTON_LABEL;
    this.generateButton.removeAttribute('aria-disabled');
  }

  disableGenerateButton() {
    if (!this.generateButton) return;
    if (this.loginRequired && this.serviceAvailable) {
      this.generateButton.disabled = false;
      this.generateButton.textContent = GENERATE_BUTTON_LABEL;
      this.generateButton.removeAttribute('aria-disabled');
      return;
    }
    this.generateButton.disabled = true;
    this.generateButton.textContent = GENERATE_BUTTON_LABEL;
    this.generateButton.setAttribute('aria-disabled', 'true');
  }

  setGenerateState(disabled, label) {
    if (!this.generateButton) return;
    this.generateButton.disabled = disabled;
    this.generateButton.textContent = label;
    this.generateButton.toggleAttribute('aria-disabled', disabled);
  }

  setUploadButtonState(disabled, label) {
    if (!this.uploadButton) return;
    this.uploadButton.disabled = disabled;
    this.uploadButton.textContent = label;
  }

  getPurchaseButtons() {
    const selectors = [
      'button[name="add"]',
      '.shopify-payment-button__button',
      '[data-shopify-buttoncontainer] button',
      'shopify-accelerated-checkout',
    ];
    const buttons = this.form ? Array.from(this.form.querySelectorAll(selectors.join(','))) : [];
    const sticky = Array.from(document.querySelectorAll('sticky-add-to-cart [ref="addToCartButton"]'));

    return [...buttons, ...sticky];
  }

  disablePurchaseButtons() {
    this.getPurchaseButtons().forEach((button) => {
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('data-mw-service-disabled', 'true');
    });
  }

  enablePurchaseButtons() {
    this.getPurchaseButtons().forEach((button) => {
      button.removeAttribute('aria-disabled');
      button.removeAttribute('data-mw-service-disabled');
    });
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.classList.toggle('mw-personalized__error--warning', message === CONTENT_SAFETY_MESSAGE);
    this.error.hidden = false;
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  hideError() {
    if (!this.error) return;
    this.error.hidden = true;
    this.error.textContent = '';
    this.error.classList.remove('mw-personalized__error--warning');
  }

  showUploadError(message) {
    if (!this.uploadError) {
      this.showError(message);
      return;
    }
    this.uploadError.textContent = message;
    this.uploadError.hidden = false;
  }

  hideUploadError() {
    if (!this.uploadError) return;
    this.uploadError.hidden = true;
    this.uploadError.textContent = '';
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

if (!customElements.get('mw-personalized-upload')) {
  customElements.define('mw-personalized-upload', MwPersonalizedUpload);
}
